// sweepstakes-entries.js — lab-aligned ribbon + tiered pill + synced FX phase (2026-04-15)

async function getActiveRaffle(supabaseUrl, supabaseKey) {
  if (window.KaidoPublicSnapshot) {
    try {
      await window.KaidoPublicSnapshot.ready;
      return window.KaidoPublicSnapshot.active() ? window.KaidoPublicSnapshot.get()?.raffle || null : null;
    } catch (_) {
      return null;
    }
  }
  try {
    const res = await fetch(`${supabaseUrl}/rest/v1/rpc/get_active_raffle`, {
      method: "POST",
      headers: {
        apikey: supabaseKey,
        Authorization: `Bearer ${supabaseKey}`,
        "Content-Type": "application/json",
      },
      body: "{}",
    });

    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data)) return data.length ? data[0] : null;
    return data && typeof data === "object" ? data : null;
  } catch (error) {
    console.error("Error fetching active raffle:", error);
    return null;
  }
}

class SweepstakesEntries {
  constructor(supabaseUrl, supabaseKey) {
    this.supabaseUrl = supabaseUrl;
    this.supabaseKey = supabaseKey;
    this.cache = new Map();
    this.pendingPromotions = new Map();
    this.snapshotEpoch = 0;
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.priceObserver = null;
    this.pageObserver = null;
    this.defaultBaseEntries = window.kaidoDefaultEntriesPerDollar();
    this.baseRatePromise = null;
    this.pendingProducts = new WeakSet();
    this.retryCounts = new WeakMap();
    this.maxRetryAttempts = 3;
    this.pendingPageKeys = new Set();
    this.pageRetryCounts = new Map();
    this.processedProducts = new Set(); // Keep track of processed cards to avoid duplicates
  }

  // Round up to the next multiple of 100 for a clean headline figure
  // (e.g. 299,988 -> 300,000). Payout is computed exactly server-side and is
  // unaffected by this display-only rounding.
  roundUpTo10(n) {
    return Math.ceil(n / 100) * 100;
  }

  // Add thousands separators (e.g., 5000 -> "5,000")
  formatWithCommas(n) {
    return n.toLocaleString();
  }

  parsePriceFromText(text) {
    const raw = String(text || "");
    const matches = raw.match(/\d+(?:[.,]\d{1,2})?/g);
    if (!matches || matches.length === 0) return NaN;
    const normalized = matches[0].replace(",", ".");
    return parseFloat(normalized);
  }

  buildFallbackPromotion(productId) {
    const base = Math.max(0, Number(this.defaultBaseEntries || window.kaidoDefaultEntriesPerDollar()));
    return {
      product_id: productId,
      base_entries: base,
      bonus_entries: 0,
      total_entries: base,
      promotion_name: null,
      has_active_promotion: false,
      __isFallback: true,
    };
  }

  // Fetches the active base entries-per-dollar rate.
  // Tries the bonus window edge function first (honors weekend bonuses like 300X),
  // then falls back to raffle_settings.entries_per_dollar.
  // Bypasses broken RPCs get_active_campaign_base_entries / get_product_promotion
  // which hardcode base=100 and read from the stale promotional_campaigns table.
  async fetchBaseRate() {
    if (window.KaidoPublicSnapshot) {
      const runtime = window.KaidoPublicSnapshot;
      await runtime.ready;
      const rate = runtime.rate();
      if (!runtime.get() || !runtime.active() || !Number.isFinite(rate) || rate <= 0) {
        throw new Error("Public entry rate unavailable");
      }
      this.defaultBaseEntries = rate;
      return rate;
    }
    if (this.baseRatePromise) return this.baseRatePromise;
    this.baseRatePromise = (async () => {
      // Attempt 1: bonus window edge function (reflects active weekend bonus)
      try {
        const res = await fetch(
          `${this.supabaseUrl}/functions/v1/get_active_bonus_window`,
          {
            method: "GET",
            headers: {
              apikey: this.supabaseKey,
              Authorization: `Bearer ${this.supabaseKey}`,
            },
          },
        );
        if (res.ok) {
          const data = await res.json();
          if (data && data.current && data.current.display_badge) {
            const match = String(data.current.display_badge).match(/(\d+)/);
            if (match) {
              const n = parseInt(match[1], 10);
              if (Number.isFinite(n) && n > 0) {
                this.defaultBaseEntries = n;
                return n;
              }
            }
          }
        }
      } catch (_) {
        // fall through
      }

      // Attempt 2: raffle_settings.entries_per_dollar
      try {
        const res = await fetch(
          `${this.supabaseUrl}/rest/v1/raffle_settings?setting_key=eq.entries_per_dollar&select=setting_value&limit=1`,
          {
            method: "GET",
            headers: {
              apikey: this.supabaseKey,
              Authorization: `Bearer ${this.supabaseKey}`,
            },
          },
        );
        if (res.ok) {
          const rows = await res.json();
          if (Array.isArray(rows) && rows[0] && rows[0].setting_value) {
            const n = parseInt(rows[0].setting_value, 10);
            if (Number.isFinite(n) && n > 0) {
              this.defaultBaseEntries = n;
              return n;
            }
          }
        }
      } catch (_) {
        // fall through
      }

      return this.defaultBaseEntries;
    })();
    return this.baseRatePromise;
  }

  // Queries product_promotions directly (anon read allowed by RLS).
  // Returns the raw row { entries_multiplier, promotion_name, end_date } or null.
  async fetchProductPromotionRow(productId) {
    if (window.KaidoPublicSnapshot) {
      await window.KaidoPublicSnapshot.ready;
      return window.KaidoPublicSnapshot.product(productId);
    }
    const url = `${this.supabaseUrl}/rest/v1/product_promotions?shopify_product_id=eq.${encodeURIComponent(String(productId))}&is_active=eq.true&select=entries_multiplier,promotion_name,end_date,source&order=created_at.desc&limit=1`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        apikey: this.supabaseKey,
        Authorization: `Bearer ${this.supabaseKey}`,
      },
    });
    if (!res.ok) throw new Error(`Product promotion request failed: ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data)) throw new Error("Invalid product promotion response");
    if (!data.length) return null;
    if (!data[0] || typeof data[0] !== "object" || Array.isArray(data[0])) {
      throw new Error("Invalid product promotion row");
    }
    return data[0];
  }

  async fetchProductPromotion(productId) {
    if (window.KaidoPublicSnapshot) return this.fetchSnapshotProductPromotion(productId);
    const cacheKey = `product_${productId}`;
    const cachedData = this.cache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < this.cacheExpiry) {
      return cachedData.data;
    }

    if (this.pendingPromotions.has(cacheKey)) {
      return this.pendingPromotions.get(cacheKey);
    }

    // Share the entire lookup across duplicate cards and the PDP until it settles.
    const request = this.fetchUncachedProductPromotion(productId, cacheKey);
    this.pendingPromotions.set(cacheKey, request);
    try {
      return await request;
    } finally {
      this.pendingPromotions.delete(cacheKey);
    }
  }

  snapshotState() {
    const runtime = window.KaidoPublicSnapshot;
    const snapshot = runtime.get();
    const now = runtime.now();
    const rate = runtime.rate();
    if (!snapshot || !runtime.active() || !Number.isFinite(now) || !Number.isFinite(rate) || rate <= 0) {
      throw new Error("Public entry snapshot unavailable");
    }
    let expiresAt = Math.min(Date.parse(snapshot.valid_until), now + this.cacheExpiry);
    // Never carry a cached product across any advertised policy boundary.
    const rows = [snapshot.raffle, ...(snapshot.windows || []), ...(snapshot.promotions || []),
      snapshot.daily, ...(snapshot.daily?.products || [])];
    for (const row of rows) {
      if (!row) continue;
      for (const date of [row.starts_at, row.ends_at]) {
        const boundary = Date.parse(date);
        if (boundary > now) expiresAt = Math.min(expiresAt, boundary);
      }
    }
    if (!Number.isFinite(expiresAt) || expiresAt <= now) throw new Error("Public entry snapshot expired");
    return {
      now, expiresAt,
      key: `${this.snapshotEpoch}:${snapshot.revision}:${snapshot.generated_at}:${snapshot.valid_until}:${rate}`,
    };
  }

  async fetchSnapshotProductPromotion(productId) {
    await window.KaidoPublicSnapshot.ready;
    const state = this.snapshotState();
    if (this.snapshotCacheKey !== state.key) {
      this.cache.clear();
      this.snapshotCacheKey = state.key;
    }
    const cacheKey = `snapshot_${state.key}_product_${productId}`;
    const cached = this.cache.get(cacheKey);
    if (cached && state.now < cached.expiresAt) return cached.data;
    if (this.pendingPromotions.has(cacheKey)) return this.pendingPromotions.get(cacheKey);

    const request = (async () => {
      const result = await this.fetchUncachedProductPromotion(productId, cacheKey);
      const current = this.snapshotState();
      if (current.key !== state.key || current.now >= state.expiresAt) {
        this.cache.delete(cacheKey);
        throw new Error("Public entry snapshot changed during lookup");
      }
      this.cache.set(cacheKey, { data: result, expiresAt: state.expiresAt });
      return result;
    })();
    this.pendingPromotions.set(cacheKey, request);
    try {
      return await request;
    } catch (error) {
      this.cache.delete(cacheKey);
      throw error;
    } finally {
      if (this.pendingPromotions.get(cacheKey) === request) this.pendingPromotions.delete(cacheKey);
    }
  }

  async fetchUncachedProductPromotion(productId, cacheKey) {
    try {
      const [baseRate, row] = await Promise.all([
        this.fetchBaseRate(),
        this.fetchProductPromotionRow(productId),
      ]);

      const safeBase = Number.isFinite(baseRate) && baseRate > 0
        ? Math.round(baseRate)
        : this.defaultBaseEntries;

      // No promotion row → product uses the plain base rate, no bonus badge.
      if (!row) {
        const result = {
          product_id: productId,
          base_entries: safeBase,
          bonus_entries: 0,
          total_entries: safeBase,
          promotion_name: null,
          promotion_end_date: null,
          has_active_promotion: false,
          source: 'manual',
          __isFallback: false,
        };
        this.cache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }

      const multiplier = Number(row.entries_multiplier);
      const safeMultiplier = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
      const totalRate = Math.round(safeBase * safeMultiplier);
      const bonus = Math.max(0, totalRate - safeBase);

      const result = {
        product_id: productId,
        base_entries: safeBase,
        bonus_entries: bonus,
        total_entries: totalRate,
        promotion_name: row.promotion_name || null,
        promotion_end_date: row.end_date || null,
        has_active_promotion: safeMultiplier > 1,
        source: row.source || 'manual',
        __isFallback: false,
      };
      this.cache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    } catch (error) {
      console.error("Error fetching product promotion:", error);
      if (window.KaidoPublicSnapshot) throw error;
      await this.fetchBaseRate();
      return this.buildFallbackPromotion(productId);
    }
  }

  /**
   * Ribbon multiplier color: gold → red as bonus increases (matches
   * quick-entries-shimmer-badge-lab.html `ribbonMultColor`, FX capped at 3×).
   */
  ribbonMultiplierColor(x) {
    const FX_CAP = 3;
    const xf = Number(x);
    if (!Number.isFinite(xf) || xf <= 1) return "#ffcf4a";
    const xCap = Math.min(xf, FX_CAP);
    if (xCap <= 1.1) return "#ffcf4a";
    const t = Math.max(0, Math.min(1, (xCap - 1.1) / (FX_CAP - 1.1)));
    const stops = [
      { p: 0, rgb: [255, 207, 74] },
      { p: 0.22, rgb: [212, 168, 18] },
      { p: 0.45, rgb: [245, 158, 11] },
      { p: 0.72, rgb: [234, 88, 12] },
      { p: 1, rgb: [220, 38, 38] },
    ];
    let j = 1;
    while (j < stops.length && t > stops[j].p) j++;
    const a = stops[j - 1];
    const b = stops[j];
    const segT = (t - a.p) / (b.p - a.p);
    const r = Math.round(a.rgb[0] + (b.rgb[0] - a.rgb[0]) * segT);
    const g = Math.round(a.rgb[1] + (b.rgb[1] - a.rgb[1]) * segT);
    const bl = Math.round(a.rgb[2] + (b.rgb[2] - a.rgb[2]) * segT);
    return `rgb(${r},${g},${bl})`;
  }

  /** Parse comma/whitespace-separated Shopify product IDs from a section data attribute. */
  parseExtraProductIds(raw) {
    return String(raw || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => /^\d+$/.test(s));
  }

  /** Align A1 image + pill nitro sweeps to one global phase (see `--kaido-fx-delay` in CSS). */
  syncFxAnimationPhase() {
    const cycleMs = 2800;
    const phase = Date.now() % cycleMs;
    document.documentElement.style.setProperty(
      "--kaido-fx-delay",
      `-${phase}ms`,
    );
  }

  /**
   * Toggle `kaido-card-a1-sweep` on the product card for image shimmer (see sweepstakes-entries.css).
   * Section `data-kaido-a1-mode`: off | every | promoted — set from product-list.liquid.
   * Quick Entries: optional `data-kaido-a1-quick-ids` — when non-empty, only those product IDs get sweep.
   */
  updateA1ImageSweep(productCard, productId, promotion) {
    if (!productCard || !productId) return;
    const section = productCard.closest(".section");
    if (!section || !section.dataset) {
      productCard.classList.remove("kaido-card-a1-sweep");
      return;
    }
    const mode = (section.dataset.kaidoA1Mode || "off").toLowerCase();
    if (mode === "off") {
      productCard.classList.remove("kaido-card-a1-sweep");
      return;
    }

    const rawQuick = section.dataset.kaidoA1QuickIds;
    const quickOnlyIds =
      rawQuick && String(rawQuick).trim()
        ? new Set(this.parseExtraProductIds(rawQuick))
        : null;
    if (
      quickOnlyIds &&
      quickOnlyIds.size > 0 &&
      section.classList.contains("kaido-section--quick-entries")
    ) {
      productCard.classList.toggle(
        "kaido-card-a1-sweep",
        quickOnlyIds.has(String(productId)),
      );
      return;
    }

    const extras = new Set(this.parseExtraProductIds(section.dataset.kaidoA1ExtraIds));
    const idStr = String(productId);
    const inExtras = extras.has(idStr);
    const rawMult =
      promotion && promotion.base_entries > 0
        ? Number(promotion.total_entries) / Number(promotion.base_entries)
        : 1;
    const isPromotedProduct =
      inExtras ||
      (promotion &&
        (promotion.source === "daily_bonus" ||
          promotion.has_active_promotion === true ||
          (Number.isFinite(rawMult) && rawMult > 1.001)));
    const show = mode === "every" || isPromotedProduct;
    productCard.classList.toggle("kaido-card-a1-sweep", Boolean(show));
  }

  createModernBadges(containerElement, promotion, price) {
    // --- Start of Diagnostic Logging ---
    // console.log("Processing product card. Raw promotion data:", promotion);
    // console.log("Product price:", price);
    // --- End of Diagnostic Logging ---

    const hadSsrTotal = Boolean(containerElement.querySelector(
      ":scope > .entries-total-badge[data-kaido-entry-ssr]",
    ));
    this.clearExistingBadges(containerElement);

    const badgeParent = containerElement;
    if (getComputedStyle(badgeParent).position === "static") {
      badgeParent.style.position = "relative";
    }

    const baseRate = Number(promotion.base_entries || 0);
    const totalRate = Number(promotion.total_entries || baseRate);

    const totalRaw = Math.ceil(price * totalRate);
    const totalEntries = this.roundUpTo10(totalRaw);
    const baseEntries = this.roundUpTo10(Math.floor(price * baseRate));
    const bonusEntries = totalEntries - baseEntries;

    const rawMultiplier = baseRate > 0 ? totalRate / baseRate : 1;
    // Check if multiplier is a whole number or has decimals
    const multiplier =
      rawMultiplier % 1 !== 0
        ? rawMultiplier.toFixed(1)
        : rawMultiplier.toFixed(0);

    const totalBadge = document.createElement("div");
    totalBadge.className = "entries-total-badge badge";
    if (hadSsrTotal) totalBadge.classList.add("is-visible");
    if (rawMultiplier > 1.0001) {
      totalBadge.classList.add("entries-total-badge--daily-bonus");
      if (rawMultiplier >= 1.5) {
        totalBadge.classList.add("entries-total-badge--pill-nitro");
      }
      if (rawMultiplier > 2.0001) {
        totalBadge.classList.add("entries-total-badge--pill-aurora");
      }
    }
    totalBadge.innerHTML = `<span class="entries-total-badge__label">${this.formatWithCommas(totalEntries)} Entries</span>`;
    badgeParent.appendChild(totalBadge);

    // --- PDP only: inject a WRAPPER containing the full-width "header" badge at
    // the top of the info flex column. The wrapper is necessary because the flex
    // container (.group-block-content) applies a `gap` between its direct children
    // — zeroing out margins on the header + banner doesn't remove that gap. By
    // wrapping BOTH into a single flex child, we take them out of the gap system
    // and can butt them flush together.
    //
    //   [ .entries-total-pdp-combo ]
    //     └─ .entries-total-badge--pdp-header  (gold)
    //     └─ .b2-badge--pdp                    (dark — inserted later)
    //   → title → price → features → ...
    //
    // On mobile the CSS hides the header variant and shows the enlarged top-right
    // pill instead.
    if (
      badgeParent.classList &&
      badgeParent.classList.contains("product-information__grid")
    ) {
      const flexCol = badgeParent.querySelector(
        ".group-block-content.layout-panel-flex--column, .group-block-content.layout-panel-flex"
      );
      if (flexCol) {
        // Clean up any stale combo wrappers or stray header badges from prior runs
        flexCol
          .querySelectorAll(".entries-total-pdp-combo")
          .forEach((el) => el.remove());
        flexCol
          .querySelectorAll(".entries-total-badge--pdp-header")
          .forEach((el) => el.remove());
        const combo = document.createElement("div");
        combo.className = "entries-total-pdp-combo";
        const headerBadge = totalBadge.cloneNode(true);
        headerBadge.classList.add("entries-total-badge--pdp-header");
        combo.appendChild(headerBadge);
        flexCol.insertBefore(combo, flexCol.firstChild);
      }
    }

    const perDollarChip = document.createElement("div");
    perDollarChip.className = "per-dollar-chip badge";
    perDollarChip.textContent = `${totalRate}x per $1`;
    // P1 design: tier-colored left border accent
    perDollarChip.style.setProperty("--chip-accent", this.ribbonMultiplierColor(rawMultiplier));
    // Place inside .card-gallery so it's positioned relative to the image area only
    // (prevents collision with the B2 banner that now sits below the image).
    const chipHost = badgeParent.querySelector(".card-gallery, card-gallery") || badgeParent;
    chipHost.appendChild(perDollarChip);

    // B2 badge — EXACT port of docs/design-previews/badge-option-b-variants.html (B2 variant)
    const hasActiveBonus = promotion.has_active_promotion && bonusEntries > 0 && rawMultiplier > 1;

    let badgeEl;
    if (hasActiveBonus) {
      const multiplierDisplay = `${multiplier}x`;
      const bonusDisplay =
        bonusEntries >= 1000
          ? `+${String(parseFloat((bonusEntries / 1000).toFixed(1)))}K`
          : `+${bonusEntries}`;
      const isDailyBonus = promotion.source === "daily_bonus";

      // Map raw multiplier to the mp-XX tier class (matches mockup CSS)
      let tierClass;
      if (rawMultiplier < 1.5) tierClass = "mp-15";
      else if (rawMultiplier < 2.0) tierClass = "mp-15";
      else if (rawMultiplier < 2.5) tierClass = "mp-20";
      else if (rawMultiplier < 3.0) tierClass = "mp-25";
      else tierClass = "mp-30";

      const strip = document.createElement("div");
      strip.className = `b2-badge badge ${tierClass}`;

      // Top line: multiplier · ⚡ · entries (exact mockup HTML)
      const topLine = document.createElement("div");
      topLine.className = "b2-top-line";

      const multSpan = document.createElement("span");
      multSpan.className = "b2-multiplier";
      multSpan.textContent = multiplierDisplay;
      topLine.appendChild(multSpan);

      const dot1 = document.createElement("span");
      dot1.className = "b2-dot";
      dot1.textContent = "\u00B7";
      topLine.appendChild(dot1);

      // Lightning bolt SVG (matches mockup)
      const bolt = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      bolt.setAttribute("class", "b2-icon bolt");
      bolt.setAttribute("viewBox", "0 0 10 16");
      bolt.setAttribute("fill", "currentColor");
      const boltPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
      boltPath.setAttribute("d", "M6 0L0 9h4.5L4 16l6-9H5.5L6 0z");
      bolt.appendChild(boltPath);
      topLine.appendChild(bolt);

      const dot2 = document.createElement("span");
      dot2.className = "b2-dot";
      dot2.textContent = "\u00B7";
      topLine.appendChild(dot2);

      const entriesSpan = document.createElement("span");
      entriesSpan.className = "b2-entries";
      // Wrap the numeric part in .b2-entries-num so CSS can style it as a
      // gold chip on PDP — makes the "+162.5K" pop against the dark banner
      // and visually ties it to the gold header above.
      entriesSpan.innerHTML = `<span class="b2-entries-num">${bonusDisplay}</span> entries`;
      topLine.appendChild(entriesSpan);

      strip.appendChild(topLine);

      // Bottom line: DAILY BONUS (only for daily bonus source)
      // Also: time-based flashing speed — the less time left, the faster the flash
      if (isDailyBonus) {
        const dailyLabel = document.createElement("span");
        dailyLabel.className = "b2-daily";
        dailyLabel.textContent = "Daily Bonus";
        strip.appendChild(dailyLabel);

        strip.classList.add("b2-badge--daily-flash");

        // Compute flash duration from promotion end_date (if available)
        // More time left → slower, more relaxed. Less time → faster, more urgent.
        const endIso = promotion.promotion_end_date || promotion.end_date;
        if (endIso) {
          const hoursLeft = (new Date(endIso).getTime() - Date.now()) / 3600000;
          let flashDur;
          if (hoursLeft > 18) flashDur = "3.6s";
          else if (hoursLeft > 12) flashDur = "2.8s";
          else if (hoursLeft > 6)  flashDur = "2.0s";
          else if (hoursLeft > 3)  flashDur = "1.3s";
          else if (hoursLeft > 1)  flashDur = "0.9s";
          else                      flashDur = "0.55s";
          strip.style.setProperty("--b2-flash-dur", flashDur);
        }
      }
      badgeEl = strip;
    } else {
      // Reserved-space placeholder — keeps grid alignment consistent across cards
      badgeEl = document.createElement("div");
      badgeEl.className = "b2-badge-placeholder";
    }

    // Insert the banner:
    //  - On PDP (.product-information__grid): inside the info-column flex container,
    //    right before the "features card" ("Entries are automatically calculated!" notice).
    //    On mobile, CSS flips order:-1 so it sits right under the product image.
    //  - On product cards: between image and title inside .product-card__content
    {
      const isPdp =
        badgeParent.classList &&
        badgeParent.classList.contains("product-information__grid");

      if (isPdp && badgeEl && badgeEl.classList) {
        badgeEl.classList.add("b2-badge--pdp");

        // Prefer appending into the .entries-total-pdp-combo wrapper so the banner
        // sits flush under the gold header (no flex `gap` between them).
        const existingCombo = badgeParent.querySelector(
          ".entries-total-pdp-combo"
        );
        const existingHeader = badgeParent.querySelector(
          ".entries-total-badge--pdp-header"
        );
        if (existingCombo) {
          existingCombo.appendChild(badgeEl);
        } else if (existingHeader && existingHeader.parentElement) {
          existingHeader.insertAdjacentElement("afterend", badgeEl);
        } else {
          let featuresHost = null;
          const notice = badgeParent.querySelector(
            ".features-card__notice, .features-card__card, .features-card__container"
          );
          if (notice) {
            let cur = notice;
            while (cur && cur.parentElement) {
              const parent = cur.parentElement;
              if (
                parent.classList &&
                parent.classList.contains("group-block-content")
              ) {
                featuresHost = cur;
                break;
              }
              cur = parent;
            }
          }
          if (featuresHost && featuresHost.parentElement) {
            featuresHost.parentElement.insertBefore(badgeEl, featuresHost);
          } else {
            const flexCol = badgeParent.querySelector(
              ".group-block-content.layout-panel-flex--column, .layout-panel-flex--column"
            );
            if (flexCol) flexCol.insertBefore(badgeEl, flexCol.firstChild);
            else badgeParent.appendChild(badgeEl);
          }
        }
      } else {
        const content = badgeParent.querySelector(".product-card__content");
        const imageWrap = content && content.querySelector(":scope > .card-gallery, :scope > card-gallery, :scope > .card__media");
        if (content && imageWrap) {
          content.insertBefore(badgeEl, imageWrap.nextSibling);
        } else if (content && content.firstChild) {
          content.insertBefore(badgeEl, content.firstChild);
        } else if (content) {
          content.appendChild(badgeEl);
        } else {
          badgeParent.appendChild(badgeEl);
        }
      }
    }

    setTimeout(() => {
      badgeParent
        .querySelectorAll(".badge")
        .forEach((el) => el.classList.add("is-visible"));
      // Badge is now inserted OUTSIDE the product-card as a sibling — find it via prev-siblings
      if (badgeEl && badgeEl.classList) badgeEl.classList.add("is-visible");
    }, 100);

    // --- PDP only: wrap title + price in a flex-ROW so they sit side-by-side
    // reliably on both desktop and mobile, with price + per-dollar stacked
    // on the right. Previous approach put .kaido-pdp-title-meta INSIDE the
    // title text-block as an absolute-positioned element, but the text-block
    // container had display:flex (from theme) and the meta stretched to full
    // container width instead of shrink-wrapping, so product-price ended up
    // overlapping DIAMOND TIER. Structural wrap is the clean fix.
    if (
      badgeParent.classList &&
      badgeParent.classList.contains("product-information__grid")
    ) {
      const titleBlock = badgeParent.querySelector(
        '[class*="heading_m7KmQQ"]'
      );
      const priceEl = badgeParent.querySelector("product-price");
      const flexCol = titleBlock && titleBlock.parentElement;

      if (titleBlock && flexCol) {
        let row = flexCol.querySelector(".kaido-pdp-title-row");
        if (!row) {
          row = document.createElement("div");
          row.className = "kaido-pdp-title-row";
          flexCol.insertBefore(row, titleBlock);
          row.appendChild(titleBlock);
        } else if (!row.contains(titleBlock)) {
          row.appendChild(titleBlock);
        }
        let meta = row.querySelector(".kaido-pdp-title-meta");
        if (!meta) {
          meta = document.createElement("div");
          meta.className = "kaido-pdp-title-meta";
          row.appendChild(meta);
        }
        if (priceEl && !meta.contains(priceEl)) {
          meta.appendChild(priceEl);
        }
        let perDollar = meta.querySelector(".kaido-pdp-per-dollar");
        if (!perDollar) {
          perDollar = document.createElement("div");
          perDollar.className = "kaido-pdp-per-dollar";
          meta.appendChild(perDollar);
        }
        const rate = Math.round(Number(totalRate) || 0);
        perDollar.innerHTML =
          '<span class="kaido-pdp-per-dollar__num">' +
          this.formatWithCommas(rate) +
          '</span>' +
          '<span class="kaido-pdp-per-dollar__unit">/ $1</span>' +
          '<span class="kaido-pdp-per-dollar__label">entries</span>';
      }
    }

    const pid =
      badgeParent.dataset && badgeParent.dataset.productId
        ? badgeParent.dataset.productId
        : null;
    if (pid) this.updateA1ImageSweep(badgeParent, pid, promotion);
  }

  clearExistingBadges(containerElement) {
    // Badge + placeholder live inside product-card__content; per-dollar chip is now inside card-gallery
    const badges = containerElement.querySelectorAll(
      ":scope > .entries-total-badge, :scope > .per-dollar-chip, :scope > .entries-bonus-ribbon, .entries-bonus-strip, .b2-badge, .b2-badge-placeholder, .card-gallery .per-dollar-chip, card-gallery .per-dollar-chip",
    );
    badges.forEach((badge) => badge.remove());

    // Also clean up any leftover B2 badges that were placed as siblings in the previous
    // (broken) external-sibling approach — prevents stale duplicates during the transition.
    const cardRoot = containerElement.closest("product-card-link") || containerElement;
    if (cardRoot && cardRoot.parentNode) {
      let sib = cardRoot.previousElementSibling;
      while (sib) {
        const prev = sib.previousElementSibling;
        if (sib.classList && (sib.classList.contains("b2-badge") || sib.classList.contains("b2-badge-placeholder"))) {
          sib.remove();
        } else {
          break;
        }
        sib = prev;
      }
    }
    const pid =
      containerElement.dataset && containerElement.dataset.productId
        ? containerElement.dataset.productId
        : null;
    if (pid) containerElement.classList.remove("kaido-card-a1-sweep");
  }

  clearPublicBadges(containerElement) {
    this.clearExistingBadges(containerElement);
    containerElement.querySelectorAll(
      ".entries-total-pdp-combo, .entries-total-badge--pdp-header, .kaido-pdp-per-dollar",
    ).forEach((element) => element.remove());
  }

  async processProductElement(productElement) {
    const snapshotEpoch = this.snapshotEpoch;
    const productId = productElement.dataset.productId;
    // The guard prevents re-processing the same element
    if (
      !productId ||
      this.processedProducts.has(productElement) ||
      this.pendingProducts.has(productElement)
    ) {
      return;
    }

    const priceElement = productElement.querySelector(".price");
    if (!priceElement) return;

    const price = this.parsePriceFromText(priceElement.textContent);
    if (isNaN(price)) return;
    this.pendingProducts.add(productElement);

    try {
      const promotion = await this.fetchProductPromotion(productId);
      this.createModernBadges(productElement, promotion, price);
      if (promotion.__isFallback) {
        const attempts = (this.retryCounts.get(productElement) || 0) + 1;
        if (attempts <= this.maxRetryAttempts) {
          this.retryCounts.set(productElement, attempts);
          setTimeout(() => this.processProductElement(productElement), attempts * 900);
        } else {
          this.processedProducts.add(productElement);
        }
      } else {
        this.retryCounts.delete(productElement);
        this.processedProducts.add(productElement);
      }
    } catch (error) {
      console.error("Error updating product card:", error);
      if (window.KaidoPublicSnapshot) this.clearPublicBadges(productElement);
    } finally {
      this.pendingProducts.delete(productElement);
      if (window.KaidoPublicSnapshot && snapshotEpoch !== this.snapshotEpoch) {
        this.processProductElement(productElement);
      }
    }
  }

  async updateProductPage() {
    const snapshotEpoch = this.snapshotEpoch;
    const productPageContainer = document.querySelector(
      ".product-information__grid",
    );
    if (!productPageContainer) return;

    const formElement = productPageContainer.querySelector(
      "product-form-component[data-product-id]",
    );
    const productId = formElement ? formElement.dataset.productId : null;

    const pageKey = `${productId || "unknown"}_page`;
    if (!productId || this.processedProducts.has(pageKey) || this.pendingPageKeys.has(pageKey))
      return;

    const priceElement = productPageContainer.querySelector(".price");
    if (!priceElement) return;

    const price = this.parsePriceFromText(priceElement.textContent);
    if (isNaN(price)) return;
    this.pendingPageKeys.add(pageKey);
    try {
      const promotion = await this.fetchProductPromotion(productId);
      this.createModernBadges(productPageContainer, promotion, price);
      if (promotion.__isFallback) {
        const attempts = (this.pageRetryCounts.get(pageKey) || 0) + 1;
        if (attempts <= this.maxRetryAttempts) {
          this.pageRetryCounts.set(pageKey, attempts);
          setTimeout(() => {
            this.processedProducts.delete(pageKey);
            this.updateProductPage();
          }, attempts * 900);
        } else {
          this.processedProducts.add(pageKey);
        }
      } else {
        this.pageRetryCounts.delete(pageKey);
        this.processedProducts.add(pageKey);
      }
    } catch (error) {
      console.error("Error updating product page badges:", error);
      if (window.KaidoPublicSnapshot) this.clearPublicBadges(productPageContainer);
    } finally {
      this.pendingPageKeys.delete(pageKey);
      if (window.KaidoPublicSnapshot && snapshotEpoch !== this.snapshotEpoch) this.updateProductPage();
    }
  }

  // NEW: A reusable function to scan the entire page for unprocessed cards.
  runFullScan() {
    document
      .querySelectorAll("product-card[data-product-id]")
      .forEach((card) => this.processProductElement(card));
    this.updateProductPage();
  }

  setupPriceObserver() {
    const priceElement = document.querySelector(
      ".product-information__grid .price",
    );
    if (!priceElement) return;

    if (this.priceObserver) this.priceObserver.disconnect();

    this.priceObserver = new MutationObserver(() => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const productPageContainer = document.querySelector(
          ".product-information__grid",
        );
        const formElement = productPageContainer.querySelector(
          "product-form-component[data-product-id]",
        );
        const productId = formElement ? formElement.dataset.productId : null;
        if (productId) {
          this.processedProducts.delete(productId + "_page");
          this.updateProductPage();
        }
      }, 100);
    });

    this.priceObserver.observe(priceElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  initPageObserver() {
    if (this.pageObserver) this.pageObserver.disconnect();
    // This observer triggers a scan whenever the page structure changes.
    this.pageObserver = new MutationObserver(() => {
      clearTimeout(this.scanDebounce);
      this.scanDebounce = setTimeout(() => this.runFullScan(), 200);
    });
    this.pageObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  init() {
    this.syncFxAnimationPhase();

    if (window.KaidoPublicSnapshot && !this.snapshotListener) {
      this.snapshotListener = () => {
        this.snapshotEpoch++;
        this.cache.clear();
        this.baseRatePromise = null;
        this.processedProducts.clear();
        if (!window.KaidoPublicSnapshot.get() || !window.KaidoPublicSnapshot.active()) {
          document.querySelectorAll("product-card[data-product-id], .product-information__grid")
            .forEach((element) => this.clearPublicBadges(element));
        }
        this.runFullScan();
      };
      document.addEventListener("kaido:public-snapshot", this.snapshotListener);
    }

    // Preload the active base rate (bonus window → raffle_settings) so fallback badges
    // don't regress to a stale hardcoded value while the first product row is fetching.
    this.fetchBaseRate().catch(() => {});

    // Run an initial scan for any static content that's ready immediately.
    this.runFullScan();

    // Set up observers for price changes and dynamically added sections.
    this.setupPriceObserver();
    this.initPageObserver();

    // NEW: Polling mechanism to act as a fallback for tricky carousels.
    // This will check 10 times over 5 seconds for any product cards that were
    // initialized late by the theme's scripts.
    let pollCount = 0;
    const pollInterval = setInterval(() => {
      this.runFullScan();
      pollCount++;
      if (pollCount >= 10) {
        clearInterval(pollInterval);
      }
    }, 500);
  }
}

document.addEventListener("DOMContentLoaded", async function () {
  const SUPABASE_URL =
    window.Shopify?.theme?.settings?.supabase_url ||
    window.SUPABASE_CONFIG?.url;
  const SUPABASE_ANON_KEY =
    window.Shopify?.theme?.settings?.supabase_anon_key ||
    window.SUPABASE_CONFIG?.anonKey;

  if (!window.KaidoPublicSnapshot && (!SUPABASE_URL || !SUPABASE_ANON_KEY)) {
    // console.error(
    //   "Supabase config missing. Check theme settings or window.SUPABASE_CONFIG"
    // );
    return;
  }

  // Only render badges when there is an active raffle
  const activeRaffle = window.KaidoPublicSnapshot || await getActiveRaffle(SUPABASE_URL, SUPABASE_ANON_KEY);
  if (!activeRaffle) {
    // console.log("No active raffle - skipping entry badge display");
    return;
  }

  const sweepstakes = new SweepstakesEntries(SUPABASE_URL, SUPABASE_ANON_KEY);
  sweepstakes.init();
});

window.SweepstakesEntries = SweepstakesEntries;
